import { useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import { articles } from "@/mocks/articles";

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();

  const article = articles.find((a) => a.slug === slug);

  useEffect(() => {
    window.scrollTo(0, 0);
    if (article) {
      document.title = `${article.title} | Pandabuy Spreadsheet`;
    } else {
      document.title = "Article Not Found | Pandabuy Spreadsheet";
    }
  }, [slug, article]);

  if (!article) {
    return (
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="pt-32 pb-20 px-6 text-center">
          <h1 className="text-2xl font-heading font-bold text-foreground-900 mb-4">Article Not Found</h1>
          <p className="text-foreground-600 mb-6">The article you are looking for does not exist or may have been moved.</p>
          <Link to="/" className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-primary-500 text-background-50 text-sm font-medium whitespace-nowrap cursor-pointer hover:bg-primary-600 transition-colors">
            <i className="ri-arrow-left-line"></i>
            Back to Homepage
          </Link>
        </main>
        <Footer />
      </div>
    );
  }

  const articleSchema = {
    "@context": "https://schema.org",
    "@type": "Article",
    "headline": article.title,
    "description": article.excerpt,
    "image": article.image,
    "datePublished": "2026-06-29",
    "dateModified": "2026-06-29",
    "author": {
      "@type": "Organization",
      "name": "Pandabuy Spreadsheet",
      "url": "https://pandabuy.makeup/"
    },
    "publisher": {
      "@type": "Organization",
      "name": "Pandabuy Spreadsheet",
      "url": "https://pandabuy.makeup/",
      "logo": {
        "@type": "ImageObject",
        "url": "https://static.readdy.ai/image/2df560051ef0cafd8dabf8a049058ba4/2ce33a4b04813007a8211abaa8144b8a.jpeg"
      }
    },
    "mainEntityOfPage": {
      "@type": "WebPage",
      "@id": `https://pandabuy.makeup/article/${article.slug}`
    }
  };

  return (
    <div className="min-h-screen bg-background-50">
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(articleSchema) }}
      />
      <Navbar />

      <main>
        <article className="pt-24 md:pt-32 pb-16 md:pb-24">
          <div className="max-w-[860px] mx-auto px-6 md:px-10">
            <nav className="mb-8" aria-label="Breadcrumb">
              <ol className="flex items-center gap-2 text-sm text-foreground-500">
                <li><Link to="/" className="hover:text-foreground-900 cursor-pointer transition-colors">Home</Link></li>
                <li><span className="mx-1">/</span></li>
                <li className="text-foreground-900 font-medium">{article.title}</li>
              </ol>
            </nav>

            <header className="mb-10 md:mb-14">
              {article.tag && (
                <span className="inline-block px-3 py-1 rounded-full bg-accent-100 text-accent-700 text-xs font-medium mb-4 whitespace-nowrap">
                  {article.tag}
                </span>
              )}
              <h1 className="text-3xl md:text-4xl lg:text-5xl font-heading font-bold text-foreground-900 leading-tight mb-4">
                {article.title}
              </h1>
              <p className="text-sm text-foreground-500">
                Published: {article.publishDate} &bull; {article.readTime} &bull; Fashion Research
              </p>
            </header>

            {article.image && (
              <div className="rounded-2xl overflow-hidden mb-10 md:mb-14">
                <img
                  src={article.image}
                  alt={article.imageAlt}
                  title={article.imageTitle}
                  className="w-full aspect-[2/1] object-cover object-top"
                />
              </div>
            )}

            <div className="prose-custom">
              {article.sections.map((section, idx) => (
                <section key={idx} className="mb-10">
                  <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900 mb-4">
                    {section.heading}
                  </h2>
                  {section.paragraphs.map((para, pIdx) => (
                    <p
                      key={pIdx}
                      className="text-sm md:text-base text-foreground-700 leading-relaxed mb-4"
                    >
                      {para}
                    </p>
                  ))}
                </section>
              ))}

              {article.faq && article.faq.length > 0 && (
                <section className="border-t border-background-200/70 pt-8 mt-12">
                  <h2 className="text-lg md:text-xl font-heading font-bold text-foreground-900 mb-3">
                    Frequently Asked Questions
                  </h2>
                  <div className="space-y-6">
                    {article.faq.map((item, fIdx) => (
                      <div key={fIdx}>
                        <h3 className="text-sm md:text-base font-semibold text-foreground-900 mb-1">
                          {item.question}
                        </h3>
                        <p className="text-sm text-foreground-600 leading-relaxed">
                          {item.answer}
                        </p>
                      </div>
                    ))}
                  </div>
                </section>
              )}
            </div>

            <div className="mt-12 pt-8 border-t border-background-200/70 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
              <Link
                to="/"
                className="inline-flex items-center gap-2 text-sm text-foreground-600 hover:text-foreground-900 cursor-pointer transition-colors"
              >
                <i className="ri-arrow-left-line"></i>
                Back to Homepage
              </Link>
              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-primary-500 text-background-50 text-sm font-medium whitespace-nowrap cursor-pointer hover:bg-primary-600 transition-colors"
              >
                Explore Spreadsheets
                <i className="ri-arrow-right-up-line"></i>
              </a>
            </div>
          </div>
        </article>
      </main>

      <Footer />
    </div>
  );
}